import { Button, Divider, Group, NumberInput, Switch, Text, TextInput } from '@mantine/core';

import { useMessageLengthConfig } from '../../api/getMessageLengthConfig';
import { FormValues, useProphetPageForm } from '../../hooks/useProphetPageForm';

import { PagesSelect } from '@/components';
import { FunnelsSelect } from '@/components/Selects/Funnels';
import config from '@/config';
import { Departments, Platforms } from '@/types';

type Props = {
  onSubmit: (data: FormValues) => void;
  isLoading: boolean;
  /** Send limits are only editable on an existing page, so the limits section renders in 'edit' only. */
  mode: 'create' | 'edit';
  initialValues?: FormValues;
  department: Departments;
  platform: Platforms;
};

export const PageForm = ({
  onSubmit,
  isLoading,
  mode,
  initialValues,
  department,
  platform,
}: Props) => {
  const form = useProphetPageForm({ initialValues });
  // The default msg/min comes from GET /config (already cached at app start),
  // so the hint never diverges from prophet's actual PAGE_RATE_LIMIT.
  const { data: systemConfig } = useMessageLengthConfig();
  const defaultPerMinute = systemConfig?.prophetRateLimitPerMinute;
  form.watch('appPage._id', () => {
    form.setFieldValue('funnels', { manager: [], completedNewDialog: [] });
  });
  const pageId = form.isValid('appPage') ? form.values.appPage._id : '';

  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <Text mt="sm" mb="xs" size="sm">
        Page ID
      </Text>
      <PagesSelect
        required
        value={form.values.appPage._id}
        onSelect={(value) => form.setFieldValue('appPage._id', value)}
        error={form.errors.appPageId}
        department={department}
        platform={platform}
        isDisabled={!!initialValues}
      />
      <Text mb="xs" mt="xl" size="sm">
        Manager funnel id
      </Text>
      <FunnelsSelect
        value={form.values.funnels.manager.map((v) => v._id)}
        required
        placeholder={'Manager funnel id'}
        pageId={pageId}
        onSelect={(value) =>
          form.setFieldValue(
            'funnels.manager',
            value.map((v) => ({ _id: v }))
          )
        }
        error={form.errors.funnels}
      />
      <Text mb="xs" mt="xl" size="sm">
        New dialog completed funnel id
      </Text>
      <FunnelsSelect
        value={form.values.funnels.completedNewDialog?.map((v) => v._id)}
        required
        placeholder={'New dialog completed funnel id'}
        pageId={pageId}
        onSelect={(value) =>
          form.setFieldValue(
            'funnels.completedNewDialog',
            value.map((v) => ({ _id: v }))
          )
        }
        error={form.errors.funnels}
      />
      <Text mb="xs" mt="xl" size="sm">
        New dialog completed with refuses funnel id
      </Text>
      <FunnelsSelect
        value={form.values.funnels.completedNewDialogWithRefuse?.map((v) => v._id)}
        required
        placeholder={'New dialog completed with refuses funnel id'}
        pageId={pageId}
        onSelect={(value) =>
          form.setFieldValue(
            'funnels.completedNewDialogWithRefuse',
            value.map((v) => ({ _id: v }))
          )
        }
        error={form.errors.funnels}
      />
      <Text mb="xs" mt="xl" size="sm">
        Underage Funnel id
      </Text>
      <FunnelsSelect
        value={form.values.funnels.underage?.map((v) => v._id)}
        required
        placeholder={'Underage funnel id'}
        pageId={pageId}
        onSelect={(value) =>
          form.setFieldValue(
            'funnels.underage',
            value.map((v) => ({ _id: v }))
          )
        }
        error={form.errors.funnels}
      />
      <Text mb="xs" mt="xl" size="sm">
        AI ID
      </Text>
      <TextInput
        value={form.values.aiId ?? ''}
        type="number"
        placeholder={'AI ID'}
        onChange={(event) => {
          // Cleared input must become undefined, not Number(undefined) = NaN —
          // NaN serializes to null and the explicit-clear intent gets lost.
          const raw = event.currentTarget.value;
          form.setFieldValue('aiId', raw ? Number(raw) : undefined);
        }}
        error={form.errors.aiId}
      />
      {mode === 'edit' && !config.isProd && (
        <>
          <Divider
            mt="xl"
            mb="md"
            labelPosition="left"
            label={<Text size="sm">Лимиты отправки</Text>}
          />
          <Switch
            label="Ночной режим"
            description="Выключен — страница отправляет сообщения круглосуточно"
            {...form.getInputProps('isNightModeEnabled', { type: 'checkbox' })}
          />
          <NumberInput
            mt="md"
            label="Сообщений в минуту"
            description={
              defaultPerMinute
                ? `Пусто — системный дефолт (${defaultPerMinute} в минуту)`
                : 'Пусто — системный дефолт'
            }
            placeholder="Дефолт системы"
            min={1}
            step={1}
            allowDecimal={false}
            allowNegative={false}
            {...form.getInputProps('limits.perMinute')}
          />
          <NumberInput
            mt="md"
            label="Сообщений в час"
            description="Скользящее окно 60 минут. Пусто — без лимита"
            placeholder="Без лимита"
            min={1}
            step={1}
            allowDecimal={false}
            allowNegative={false}
            {...form.getInputProps('limits.perHour')}
          />
          <NumberInput
            mt="md"
            label="Сообщений в сутки"
            description="Календарные сутки в таймзоне департамента. Пусто — без лимита"
            placeholder="Без лимита"
            min={1}
            step={1}
            allowDecimal={false}
            allowNegative={false}
            {...form.getInputProps('limits.perDay')}
          />
        </>
      )}
      <Group mt="xl" justify="right">
        <Button loading={isLoading} type="submit">
          {mode === 'edit' ? 'Сохранить' : 'Создать'}
        </Button>
      </Group>
    </form>
  );
};
